<template>
  <NuxtLayout name="standard">
    <ConnectifTag :name="CONNECTIF_PAGE_TAGS.DETAIL" />
    <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.TRANSACTIONAL" />
    <ConnectifCategory :name="province" />
    <template v-if="cottagePage">
      <CottageSectionGallery
        :item="{
          id: cottagePage.id,
          media: cottagePage.media,
          name: cottagePage.name,
          village: cottagePage.village,
          province: cottagePage.province,
        }"
      />

      <TContainer :mobile-full="true">
        <div class="lg:relative lg:z-0 lg:flex lg:flex-row-reverse lg:gap-x-4">
          <div class="lg:sticky lg:z-50 lg:top-8 lg:w-4/12">
            <div
              class="lg:h-full lg:absolute lg:z-0 lg:top-0 lg:bottom-0 lg:left-0 lg:right-0 lg:mx-auto lg:flex lg:w-full lg:items-start lg:justify-end"
            >
              <aside class="lg:w-full lg:sticky lg:top-16 lg:shrink-0">
                <CottageAside
                  :item="{
                    id: cottagePage.id,
                    name: cottagePage.name,
                    slug: cottagePage.slug,
                    province: cottagePage.province,
                    calendarUpdate: cottagePage.calendarUpdate,
                  }"
                />
              </aside>
            </div>
          </div>
          <div class="lg:w-8/12">
            <LazyVisibleCottageSectionInfo
              hydrate-on-visible
              :item="{
                name: cottagePage.name,
                slug: cottagePage.slug,
                rentType: cottagePage.rentType,
                capacity: cottagePage.capacity,
                numRooms: cottagePage.numRooms,
                numBeds: cottagePage.numBeds,
                village: cottagePage.village,
                province: cottagePage.province,
                review: cottagePage.review,
                salesService: cottagePage.salesService,
                isReviewsEnabled: cottagePage.isReviewsEnabled,
              }"
            />

            <LazyVisibleCottageSectionDeals
              hydrate-on-visible
              :item="{
                deals: cottagePage.deals,
                province: cottagePage.province,
                slug: cottagePage.slug,
              }"
            />

            <LazyVisibleCottageSectionMoreInfo
              hydrate-on-visible
              :item="{
                description: cottagePage.description,
                externalLinks: cottagePage.externalLinks,
                salesService: cottagePage.salesService,
                averagePriceAmount: cottagePage.averagePriceAmount,
              }"
            />

            <LazyVisibleCottageSectionCharacteristics
              hydrate-on-visible
              :item="{
                features: {
                  exteriors: cottagePage.features.exteriors,
                  interiors: cottagePage.features.interiors,
                  services: cottagePage.features.services,
                  accessibilities: cottagePage.features.accessibilities,
                  situations: cottagePage.features.situations,
                  qualityCertifications:
                    cottagePage.features.qualityCertifications,
                },
              }"
            />

            <LazyVisibleCottageSectionActivities
              hydrate-on-visible
              :item="{
                activityDetail: cottagePage.activityDetail,
              }"
            />

            <LazyVisibleCottageSectionInterests
              hydrate-on-visible
              :item="{
                pointsOfInterest: cottagePage.pointsOfInterest,
              }"
            />

            <LazyCottageSectionMap
              :item="{
                latitude: cottagePage.latitude,
                longitude: cottagePage.longitude,
                address: cottagePage.address,
                village: cottagePage.village,
                province: cottagePage.province,
                directions: cottagePage.directions,
              }"
            />

            <LazyVisibleCottageSectionReviews
              v-if="cottagePage.isReviewsEnabled && cottagePage.reviews"
              hydrate-on-visible
              :item="{
                reviews: cottagePage.reviews,
                slug: cottagePage.slug,
                province: cottagePage.province,
                review: cottagePage.review,
              }"
            />

            <LazyVisibleCottageSectionOwner
              hydrate-on-visible
              :item="{
                owner: cottagePage.owner,
                salesService: cottagePage.salesService,
                features: cottagePage.features,
                calendarUpdate: cottagePage.calendarUpdate,
              }"
            />

            <LazyVisibleCottageSectionShare hydrate-on-visible />

            <LazyVisibleCottageSectionRegisterNumber
              hydrate-on-visible
              :item="{
                tourismRegistrationNumber:
                  cottagePage.tourismRegistrationNumber,
                owner: cottagePage.owner,
                salesService: cottagePage.salesService,
                registrationNumbers: cottagePage.registrationNumbers,
              }"
            />

            <LazyVisibleCottageSectionReportError hydrate-on-visible />
          </div>
        </div>
      </TContainer>

      <LazyVisibleCottageSectionRecommendedA
        hydrate-on-visible
        :item="{
          recommended: cottagePage.recommended,
        }"
      />
      <LazyVisibleCottageSectionRecommendedB
        hydrate-on-visible
        :item="{
          recommended: cottagePage.recommended,
        }"
      />

      <LazyVisibleCottageCtaKeepSearching
        hydrate-on-visible
        :item="{
          province: cottagePage.province,
        }"
      />

      <LazyVisibleCottageSectionSeoNearbyCorners
        hydrate-on-visible
        :item="{
          seoLinks: cottagePage.seoLinks,
        }"
      />

      <LazyVisibleCottageSectionSeoNearbyVillages
        hydrate-on-visible
        :item="{
          seoLinks: cottagePage.seoLinks,
        }"
      />

      <LazyVisibleCottageSectionSeoNearbyZones
        hydrate-on-visible
        :item="{
          seoLinks: cottagePage.seoLinks,
        }"
      />

      <SSpacer />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Entities
import { ALIAS_PATH } from '~/modules/views/cottage/constants'
import type { CottagePage } from '~/modules/views/cottage/entities/CottagePage'

// Composables
import { useToCottagePage } from '~/modules/views/cottage/composables/transformers/useToCottagePage'
import { usePageConfiguration } from '~/modules/views/cottage/composables/usePageConfiguration'
import { usePageAnalytics } from '~/modules/views/cottage/composables/usePageAnalytics'
import { usePageRedirects } from '~/modules/views/cottage/composables/usePageRedirects'
import { sendConnectifPageProduct } from '~/modules/integrations/connectif/composables/useConnectifComposable'

// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~/modules/integrations/connectif/constants'
import { CONNECTIF_DEFAULT_PRICE } from '~/modules/integrations/connectif/constants/index'

// Fetchers
import fetchRuralityOwnersDetail from '~/modules/api/rurality/v3/owners/detail'
import fetchRuralityCottagesDetail from '~/modules/api/rurality/v3/cottages/detail'
import fetchRuralityCottagesSeoLinks from '~/modules/api/rurality/v3/cottages/seo-links'
import fetchRuralityCottagesRecommended from '~/modules/api/rurality/v3/cottages/recommended-by-cottage/detail'
import type { schemaType as cottagesRecommendedSchemaType } from '~/modules/api/rurality/v3/cottages/recommended-by-cottage/detail/schema'
import fetchRuralityReviewsDetail from '~/modules/api/rurality/v3/cottage-reviews/cottage/detail'
import fetchRuralityDealsDetail from '~/modules/api/rurality/v3/cottage-deals/cottage/detail'
import fetchRuralityStatsCottage from '~/modules/api/rurality/v3/stats/cottage/new'
import { MenuPublicCottages } from '~/modules/menu/constants/public-menu'

/* COMPOSABLES */
const route = useRoute()
const { fromRurality } = useToCottagePage()
const { showErrorPage, redirectDeletedPage } = usePageRedirects()

/* PAGE META */
definePageMeta({
  alias: ALIAS_PATH,
  publicMenuItem: MenuPublicCottages,
})

/* CONSTANTS */
const slug = route.params.slug as string
const province = route.params.province as string
let cottagePage: CottagePage | null = null

/* START */
const cottageData = await fetchRuralityCottagesDetail({ slug, province })

if (cottageData.success) {
  const getCottageCall = cottageData.success.data

  const ownerData = await fetchRuralityOwnersDetail({
    id: getCottageCall.owner.id,
  })

  if (ownerData.success) {
    const seoLinks = await fetchRuralityCottagesSeoLinks({
      id: getCottageCall.id,
    })

    let recommended: cottagesRecommendedSchemaType | null = null
    if (getCottageCall.salesService.isShowRecommendedCottage) {
      const recommendedCall = await fetchRuralityCottagesRecommended({
        id: getCottageCall.id,
      })
      if (recommendedCall.success) {
        recommended = recommendedCall.success.data
      } else if (recommendedCall.error) {
        showErrorPage(recommendedCall.error as ApiResponseError)
      }
    }

    const reviewsData = await fetchRuralityReviewsDetail({
      id: getCottageCall.id,
      limit: 4,
    })

    const dealsData = await fetchRuralityDealsDetail({
      id: getCottageCall.id,
      limit: 4,
    })

    cottagePage = fromRurality({
      cottagesDetail: getCottageCall,
      ownersDetail: ownerData.success.data,
      cottagesRecommended: recommended,
      seoLinks: seoLinks.success ? seoLinks.success.data : null,
      reviews: reviewsData.success ? reviewsData.success.data : null,
      deals: dealsData.success ? dealsData.success.data : null,
    })

    if (cottagePage) {
      usePageConfiguration(cottagePage)
    }
  } else if (ownerData.error) {
    showErrorPage(ownerData.error as ApiResponseError)
  }
} else if (cottageData.error) {
  if (cottageData.error.statusCode === 404) {
    showErrorPage(cottageData.error as ApiResponseError, 410)
  } else if (cottageData.error.statusCode === 412) {
    await redirectDeletedPage(province)
  } else {
    showErrorPage(cottageData.error as ApiResponseError)
  }
}

const sendStatsCottage = async (cottagePage: CottagePage) => {
  await fetchRuralityStatsCottage({
    cottageId: cottagePage.id,
    eventType: 'card_viz',
  })
}

/* BROWSER START */
onMounted(() => {
  if (cottagePage) {
    usePageAnalytics(cottagePage)
    if (import.meta.client) {
      sendStatsCottage(cottagePage)

      sendConnectifPageProduct({
        ...cottagePage,
        productPrice: CONNECTIF_DEFAULT_PRICE.CONTACT,
      })
    }
  }
})
</script>
